import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Clock, CheckCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const Hero = () => {
  const [address, setAddress] = useState("");
  const navigate = useNavigate();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (address.trim()) {
      navigate(`/get-offer?address=${encodeURIComponent(address)}`);
    }
  };

  const handleAddressSelect = (place) => {
    // Navigate directly with the selected address from Google Places
    if (place && place.formatted_address) {
      navigate(`/get-offer?address=${encodeURIComponent(place.formatted_address)}`);
    }
  };

  return (
    <section className="relative min-h-screen pt-16 md:pt-20 overflow-hidden" data-testid="hero-section">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1614141476243-390eb3245417?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        }}
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 hero-gradient" />
      
      {/* Subtle glow */}
      <div className="absolute inset-0 glow-effect" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="max-w-xl"
          >
            {/* Trust Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-slate-900/5 backdrop-blur-sm border border-slate-200 rounded-full px-4 py-2 mb-6"
            >
              <Clock className="w-4 h-4 text-[#D32F2F]" />
              <span className="text-sm font-medium text-slate-700">24/7 Available • Nationwide</span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight tracking-tight mb-6"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              We Buy Houses{" "}
              <span className="text-[#D32F2F]">Fast</span>
              <br />
              For Cash
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed"
            >
              Fair cash offers. No realtors. No repairs needed. 
              Close on your timeline and move forward stress-free.
            </motion.p>

            {/* Benefits List */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="flex flex-wrap gap-x-6 gap-y-3 mb-8"
            >
              {["No Fees", "No Commissions", "Any Condition"].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#D32F2F]" />
                  <span className="text-slate-700 font-medium">{item}</span>
                </div>
              ))}
            </motion.div>

            {/* Address Search Bar with Autocomplete */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
            >
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
                <AddressAutocomplete
                  value={address}
                  onChange={setAddress}
                  onSelect={handleAddressSelect}
                  placeholder="Enter your property address..."
                  className="flex-1"
                  inputClassName="h-14 text-lg border-slate-200 bg-white shadow-sm focus:border-[#D32F2F] focus:ring-[#D32F2F]/20 rounded-xl"
                  testId="hero-address-input"
                />
                <Button 
                  type="submit"
                  className="bg-[#D32F2F] hover:bg-[#B71C1C] text-white font-bold text-lg h-14 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all btn-primary"
                  data-testid="hero-get-offer-btn"
                >
                  Get Cash Offer
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </form>
              <p className="text-sm text-slate-500 mt-3">
                Get a no-obligation cash offer in 24 hours
              </p>
            </motion.div>

            {/* Trust Badges */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="mt-10 pt-8 border-t border-slate-200/50"
            >
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-4 font-medium">Trusted & Verified</p>
              <div className="flex flex-wrap items-center gap-6 md:gap-10">
                {/* Google Rating */}
                <div className="flex items-center gap-3 bg-white/80 backdrop-blur-sm rounded-xl px-4 py-3 shadow-sm border border-slate-100">
                  <svg viewBox="0 0 24 24" className="w-7 h-7" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <div>
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                        </svg>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">5.0 Rating</p>
                  </div>
                </div>

                {/* BBB Accredited */}
                <div className="flex items-center gap-3 bg-white/80 backdrop-blur-sm rounded-xl px-4 py-3 shadow-sm border border-slate-100">
                  <svg viewBox="0 0 85 55" className="h-12 w-auto" xmlns="http://www.w3.org/2000/svg">
                    <rect width="85" height="55" fill="white"/>
                    <rect x="0" y="38" width="85" height="17" fill="#005891"/>
                    <path d="M42.5 5 L42.5 8 Q42.5 4 45 4 Q47.5 4 47.5 8 L47.5 5 Q47.5 2 42.5 2 Q37.5 2 37.5 5 L37.5 8 Q37.5 4 40 4 Q42.5 4 42.5 8 Z" fill="#005891"/>
                    <rect x="41" y="8" width="3" height="18" fill="#005891"/>
                    <circle cx="42.5" cy="6" r="4" fill="#005891"/>
                    <ellipse cx="42.5" cy="4" rx="2" ry="3" fill="#F7941D"/>
                    <text x="42.5" y="35" fontFamily="Arial, sans-serif" fontSize="12" fontWeight="bold" fill="#005891" textAnchor="middle">BBB</text>
                    <text x="42.5" y="48" fontFamily="Arial, sans-serif" fontSize="5" fontWeight="bold" fill="white" textAnchor="middle">ACCREDITED</text>
                    <text x="42.5" y="53" fontFamily="Arial, sans-serif" fontSize="5" fontWeight="bold" fill="white" textAnchor="middle">BUSINESS</text>
                  </svg>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                      </svg>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Content - Hero Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="hidden lg:block"
          >
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/8482895/pexels-photo-8482895.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Happy couple celebrating with house keys"
                className="rounded-2xl shadow-2xl w-full object-cover"
                style={{ maxHeight: '500px' }}
              />
              {/* Floating Badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 border border-slate-100"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Average Close Time</p>
                    <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>7-14 Days</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
